import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

// Customer-facing subscription charts. Reuses the admin chart_data JSON shape
// but renders a focused set of charts: units/files/size, eHÁZ synchronized
// houses, and monthly billing history. All visible labels come from data
// values so the view can pass localized strings.
export default class extends Controller {
  static targets = ["unitsFilesChart", "ehazChart", "billingChart"]
  static values = {
    chartsDataUrl: String,
    labels: Object,
    currency: { type: String, default: "HUF" }
  }

  connect() {
    this.charts = []
    this.loadChartData()
  }

  disconnect() {
    this.charts.forEach((c) => c.destroy())
    this.charts = []
  }

  async loadChartData() {
    try {
      const response = await fetch(this.chartsDataUrlValue, {
        headers: { Accept: "application/json" }
      })
      const data = await response.json()

      if (this.hasUnitsFilesChartTarget) {
        this.createUnitsFilesChart(data.stacked_data, data.stacked_labels)
      }
      if (this.hasEhazChartTarget && data.line_data) {
        this.createEhazChart(data.line_data, data.line_labels)
      }
      if (this.hasBillingChartTarget && data.billing_data) {
        this.createBillingChart(data.billing_data)
      }
    } catch (error) {
      console.error("Error loading subscription chart data:", error)
    }
  }

  t(key, fallback) {
    return (this.labelsValue && this.labelsValue[key]) || fallback
  }

  sumSeries(stackedData, labels, metric) {
    const keys = Object.keys(stackedData)
    return labels.map((date) =>
      keys.reduce((sum, k) => sum + ((stackedData[k][date] && stackedData[k][date][metric]) || 0), 0)
    )
  }

  createUnitsFilesChart(stackedData, labels) {
    const ctx = this.unitsFilesChartTarget.getContext("2d")
    const buildings = this.sumSeries(stackedData, labels, "buildings")
    const files = this.sumSeries(stackedData, labels, "files")
    const filesSize = this.sumSeries(stackedData, labels, "filesSize")

    this.charts.push(new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: this.t("building_complexes", "Társasházak"),
            type: "bar",
            data: buildings,
            backgroundColor: "rgba(54, 162, 235, 0.7)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: this.t("files", "Fájlok száma"),
            type: "bar",
            data: files,
            backgroundColor: "rgba(75, 192, 192, 0.7)",
            borderColor: "rgba(75, 192, 192, 1)",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: this.t("files_size", "Tárhely (GB)"),
            type: "line",
            data: filesSize.map((b) => b / (1024 * 1024 * 1024)),
            borderColor: "rgba(255, 159, 64, 1)",
            backgroundColor: "rgba(255, 159, 64, 0.1)",
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            yAxisID: "y1"
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { position: "top" } },
        scales: {
          y: { type: "linear", position: "left", beginAtZero: true, ticks: { precision: 0 } },
          y1: {
            type: "linear",
            position: "right",
            beginAtZero: true,
            grid: { drawOnChartArea: false },
            title: { display: true, text: this.t("files_size", "Tárhely (GB)") },
            ticks: { callback: (v) => v.toFixed(1) }
          }
        }
      }
    }))
  }

  createEhazChart(lineData, labels) {
    const ctx = this.ehazChartTarget.getContext("2d")
    const synced = this.sumSeries(lineData, labels, "ehaz_synchronized_houses")

    this.charts.push(new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [{
          label: this.t("ehaz_synchronized", "eHÁZ szinkronizált házak"),
          data: synced,
          borderColor: "rgba(220, 53, 69, 1)",
          backgroundColor: "rgba(220, 53, 69, 0.1)",
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "rgba(220, 53, 69, 1)",
          pointBorderColor: "#fff",
          pointBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "top" } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
      }
    }))
  }

  createBillingChart(billingData) {
    const ctx = this.billingChartTarget.getContext("2d")
    const monthNames = ["Jan", "Feb", "Már", "Ápr", "Máj", "Jún", "Júl", "Aug", "Szept", "Okt", "Nov", "Dec"]
    const currency = this.currencyValue

    const displayLabels = billingData.labels.map((label) => {
      const [year, month] = label.split(".")
      return `${monthNames[parseInt(month) - 1]} ${year}`
    })

    const fmt = (v) => new Intl.NumberFormat("hu-HU", {
      style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(v)

    this.charts.push(new Chart(ctx, {
      type: "bar",
      data: {
        labels: displayLabels,
        datasets: [
          {
            label: this.t("billed", "Kiszámlázott (HUF)"),
            type: "bar",
            data: billingData.data.map((i) => i.all_invoices),
            backgroundColor: "rgba(13, 110, 253, 0.7)",
            borderColor: "rgba(13, 110, 253, 1)",
            borderWidth: 1
          },
          {
            label: this.t("paid", "Befizetett (HUF)"),
            type: "line",
            data: billingData.data.map((i) => i.paid_invoices),
            borderColor: "rgba(40, 167, 69, 1)",
            backgroundColor: "rgba(40, 167, 69, 0.1)",
            borderWidth: 3,
            fill: false,
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: "rgba(40, 167, 69, 1)",
            pointBorderColor: "#fff",
            pointBorderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: { label: (c) => `${c.dataset.label}: ${fmt(c.parsed.y)}` }
          }
        },
        scales: {
          x: { ticks: { maxRotation: 45, minRotation: 45 } },
          y: { beginAtZero: true, ticks: { callback: (v) => fmt(v) } }
        }
      }
    }))
  }
}
